import {isFeatureEnabled} from '@github-ui/feature-flags'
import {getQueryClient} from '@github-ui/query-client'
import {useLoaderData, useRouteLoaderData} from '@github-ui/react-router'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {skipToken, type UseQueryResult, useSuspenseQuery, type UseSuspenseQueryResult} from '@tanstack/react-query'

import type {RouteQueryKey} from '../query-key'
import type {
  ConfigQueryResultData,
  QueryOverridesFor,
  QueryRouteQueryConfig,
  SuspenseQueryOverridesFor,
  UseQueriesConfigOptions,
  UseRouteQueryResult,
} from './data-router-types'
import {QueryRouteQueryType} from './data-router-types'
import {type QueryRoute, type RouteMatchData, useRouteMatches} from './query-route'
import {useStreamingCompatibleQuery} from './use-streaming-compatible-query'

/**
 * Pure predicate: should a blocking route query *suspend* on read instead of
 * returning `undefined`? True only on a true cold miss — a blocking query
 * whose first fetch is in flight with no data yet (`isLoading`). This
 * intentionally excludes:
 *  - queries that already have data (stale or fresh) → served immediately,
 *    revalidated in the background: stale-while-revalidate, no suspend.
 *  - disabled (`enabled: false`) queries → `isLoading` is false when not
 *    fetching, so they never suspend (and never hang).
 *  - errored queries → surfaced via `isError`, not swallowed by a fallback.
 *
 * NOTE: `suppressDuringSSR` is intentionally NOT a parameter here. It is a
 * *persistent* route marker that stays `true` after hydration for streaming
 * routes (registered via `registerRouteWithUIService`). Checking it here
 * would permanently disable cold-miss suspense for those routes on the client.
 * SSR gating is handled by the caller's `!isServer` check.
 *
 * Extracted for unit testing.
 */
export function shouldSuspendBlockingColdMiss({
  type,
  result,
}: {
  type: QueryRouteQueryType
  result: Pick<UseQueryResult<unknown>, 'data' | 'isLoading' | 'isError'>
}): boolean {
  return type === QueryRouteQueryType.Blocking && result.isLoading && result.data === undefined && !result.isError
}

/**
 * Default for the `TData` generic on the route query hooks. Mirrors `ConfigQueryResultData`
 * so callers who do not narrow via `select` get the query's fetched data type unchanged.
 */
type DefaultQueryData<
  Config extends QueryRoute<
    string,
    string,
    string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Record<string, QueryRouteQueryConfig<string, any, any, any, any, any, any>>
  >,
  QueryName extends string & keyof Config['queries'],
> = ConfigQueryResultData<Config, QueryName>

function withQueryKey<Result extends object, QueryKey extends RouteQueryKey | undefined>(
  result: Result,
  queryKey: QueryKey,
): Result & {queryKey: QueryKey} {
  if (!isFeatureEnabled('react_query_props_with_key')) {
    return {...result, queryKey}
  }

  // Wrap result in a Proxy to add queryKey without spreading or mutating TanStack's tracked result.
  // Spreading would subscribe the component to every property change; the Proxy preserves TanStack's
  // fine-grained property tracking by only intercepting queryKey reads.
  return new Proxy(result, {
    get(target, property, receiver) {
      // Override queryKey reads, otherwise defer to default property read (preserves TanStack tracking).
      return property === 'queryKey' ? queryKey : Reflect.get(target, property, receiver)
    },
  }) as Result & {queryKey: QueryKey}
}

/**
 * Resolves a route's query configurations, distinguishing caller bugs from data that is simply not
 * there yet.
 *
 * Reading an unmounted route, or a child route without opting in, is a mistake in the calling code
 * and still throws. A mounted route with no usable loader data is not:
 * `useMatches` includes matches whose loader has not resolved (`loaderData` is then `undefined`,
 * where React Router only surfaces settled matches), a match can belong to a route with a
 * hand-written loader that never returns `queries`, and a child route's loader can fail outright.
 * That state is reported as `undefined` so callers who can tolerate it do not need an error
 * boundary to contain an exception.
 *
 * Every hook runs before any of these checks so the hook order stays stable across renders.
 */
function useOptionalQueriesConfigs<
  Config extends QueryRoute<
    string,
    string,
    string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Record<string, QueryRouteQueryConfig<string, any, any, any, any, any, QueryRouteQueryType>>
  >,
>(
  routeArg: Config,
  {allowReadFromChildRoutes}: UseQueriesConfigOptions = {},
): {queries: RouteMatchData<Config>['queries'] | undefined; mountedRouteIds: string} {
  const matches = useRouteMatches()
  const {routeId: currentRouteId} = useLoaderData()
  const routeMatchData = useRouteLoaderData<RouteMatchData<Config> | undefined>(routeArg.id)

  const mountedRouteIds = matches.map(m => m.id).join(', ')

  const argRouteIndex = matches.findIndex(r => r.id === routeArg.id)
  if (argRouteIndex === -1) {
    throw new Error(
      `Cannot read data from unmounted route with ID "${routeArg.id}". Mounted route IDs are: ${mountedRouteIds}`,
    )
  }

  const currentRouteIndex = matches.findIndex(r => r.id === currentRouteId)
  if (!allowReadFromChildRoutes && argRouteIndex > currentRouteIndex) {
    throw new Error(
      `Cannot read data from child route with ID "${routeArg.id}" from parent route "${currentRouteId}". Use { allowReadFromChildRoutes: true } option to enable this.  Mounted route IDs are: ${mountedRouteIds}`,
    )
  }

  // Checked rather than destructured: a hand-written loader can resolve to a defined value that has
  // no `queries` key at all, which a plain null check would let through to fail one line later.
  return {queries: routeMatchData?.queries, mountedRouteIds}
}

/**
 * Returns the query configurations for a given route.
 * If called from the wrong route, or the route has no query loader data, throws an error.
 */
export function useQueriesConfigs<
  Config extends QueryRoute<
    string,
    string,
    string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Record<string, QueryRouteQueryConfig<string, any, any, any, any, any, QueryRouteQueryType>>
  >,
>(routeArg: Config, options: UseQueriesConfigOptions = {}): RouteMatchData<Config>['queries'] {
  const {queries, mountedRouteIds} = useOptionalQueriesConfigs(routeArg, options)
  if (!queries) {
    throw new Error(
      `No query route loader data for mounted route with ID "${routeArg.id}" — its loader has not resolved yet, or it is not a query route. Mounted route IDs are: ${mountedRouteIds}`,
    )
  }

  return queries
}

/**
 * Returns a single query configuration by name.
 *
 * The returned shape is the loader entry augmented with `queryConfig` (the
 * TanStack Query options to feed into `useQuery` / `useSuspenseQuery`).
 * `queryConfig` is intentionally absent from `RouteMatchData` because it
 * isn't JSON-serializable; we rebuild it on demand from the route
 * definition via `route.buildQueryOptions`, using the serializable
 * `queryKey` from loader data as the source of truth.
 */
export function useQueriesConfig<
  Config extends QueryRoute<
    string,
    string,
    string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Record<string, QueryRouteQueryConfig<string, any, any, any, any, any, QueryRouteQueryType>>
  >,
  QueryName extends string & keyof Config['queries'],
>(route: Config, queryName: QueryName, options?: UseQueriesConfigOptions) {
  const ctx = useQueriesConfigs(route, options)
  return buildQueriesConfig(route, queryName, ctx)
}

/**
 * `useQueriesConfig` for callers that can render without the route's data.
 *
 * Returns `undefined` when the route is mounted but has no query loader data. An unknown query
 * name still throws — that is a config error in the caller, not a missing-data state.
 */
function useOptionalQueriesConfig<
  Config extends QueryRoute<
    string,
    string,
    string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Record<string, QueryRouteQueryConfig<string, any, any, any, any, any, QueryRouteQueryType>>
  >,
  QueryName extends string & keyof Config['queries'],
>(route: Config, queryName: QueryName, options?: UseQueriesConfigOptions) {
  const {queries} = useOptionalQueriesConfigs(route, options)
  return queries && buildQueriesConfig(route, queryName, queries)
}

function buildQueriesConfig<
  Config extends QueryRoute<
    string,
    string,
    string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Record<string, QueryRouteQueryConfig<string, any, any, any, any, any, QueryRouteQueryType>>
  >,
  QueryName extends string & keyof Config['queries'],
>(route: Config, queryName: QueryName, queries: RouteMatchData<Config>['queries']) {
  const entry = queries[queryName]
  if (!entry) {
    const validQueryNames = Object.keys(queries).join(', ')
    throw new Error(
      `No query named "${queryName}" on route with ID "${route.id}". Queries on this route are: ${validQueryNames}`,
    )
  }
  const queryConfig = route.buildQueryOptions(queryName, entry.queryKey)
  // `entry.suppressDuringSSR` is set by every loader (canonical loaders use false;
  // `registerRouteWithUIService` sets true for streaming registrations) so we can
  // pass it straight through to `useStreamingCompatibleQuery`.
  return {...entry, queryConfig}
}

/**
 * Given a named route query and a route, returns the
 * result of calling useQuery on the generated route config.
 *
 * Overrides for query configuration can be optionally passed as a third argument
 *
 * When called on a blocking route, data is always defined.
 * When called on a deferred route, data is potentially undefined.
 */
export function useRouteQuery<
  Config extends QueryRoute<
    string,
    string,
    string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Record<string, QueryRouteQueryConfig<string, any, any, any, any, any, any>>
  >,
  QueryName extends string & keyof Config['queries'],
  TData = DefaultQueryData<Config, QueryName>,
>(route: Config, queryName: QueryName, queryOverrides?: QueryOverridesFor<Config, QueryName, TData>) {
  const {queryConfig, suppressDuringSSR, type} = useQueriesConfig(route, queryName)
  /**
   * If we have a deferred query we don't have initial data always defined, so it potentially
   * could render with `undefined`.  When we have a blocking query, the loader awaits the data
   * so it should never be undefined
   */
  // `useStreamingCompatibleQuery` is a no-op wrapper around `useQuery` when
  // `suppressDuringSSR` is false (the canonical case). Routing through it
  // here lets streaming-capable routes (UI Service-registered ones, where the
  // loader sets `suppressDuringSSR: true` on the loaderData entry) get SSR
  // suppression automatically without a separate code path.
  const result = useStreamingCompatibleQuery(
    // @ts-expect-error not sure
    {...queryConfig, ...queryOverrides},
    suppressDuringSSR,
  )

  const {isServer} = useRuntimeEnvironment()

  // SWR-preserving suspend on a true cold miss. When the query has
  // no in-memory data yet (garbage-collected while unobserved, then re-read on a
  // soft navigation), rendering would otherwise return `undefined` and break the
  // page. Suspending here does NOT discard data: the thrown promise dedupes with
  // the in-flight fetch and, via the IndexedDB persister, resolves with stale
  // data before revalidating in the background — stale-while-revalidate. When
  // any cached/stale data already exists, `isLoading` is false so we never
  // suspend and serve it immediately. Never runs on the server (blocking data is
  // seeded from the embedded SSR payload there).
  if (!isServer && shouldSuspendBlockingColdMiss({type, result})) {
    // @ts-expect-error queryConfig is loosely typed here, same as the useQuery call above
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- throwing a promise is the React Suspense mechanism.
    throw getQueryClient().ensureQueryData({...queryConfig, ...queryOverrides})
  }

  return withQueryKey(result, queryConfig.queryKey) as unknown as UseRouteQueryResult<
    Config['queries'][QueryName],
    TData
  > & {
    queryKey: RouteQueryKey
  }
}

/**
 * Given a named route query and a route, returns the
 * result of calling useSuspenseQuery on the generated route config.
 *
 * Overrides for query configuration can be optionally passed as a third argument
 *
 * When called on a blocking route, data is always defined.
 * When called on a deferred route, data is potentially undefined.
 */
export function useSuspenseRouteQuery<
  Config extends QueryRoute<
    string,
    string,
    string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Record<string, QueryRouteQueryConfig<string, any, any, any, any, any, any>>
  >,
  QueryName extends string & keyof Config['queries'],
  TData = DefaultQueryData<Config, QueryName>,
>(route: Config, queryName: QueryName, queryOverrides?: SuspenseQueryOverridesFor<Config, QueryName, TData>) {
  const {queryConfig} = useQueriesConfig(route, queryName)
  /**
   * If we have a deferred query we don't have initial data always defined, so it potentially
   * could render with `undefined`.  When we have a blocking query, the loader awaits the data
   * so it should never be undefined
   */
  return withQueryKey(
    useSuspenseQuery(
      // @ts-expect-error not sure
      {...queryConfig, ...queryOverrides},
    ),
    queryConfig.queryKey,
  ) as UseSuspenseQueryResult<TData> & {
    queryKey: RouteQueryKey
  }
}

/**
 * Given a named route query and a route, returns the
 * result of calling useQuery on the generated route config.
 *
 * Should be used in scenarios where a parent route's component wants to access data from
 * a child route. The parent component is responsible for ensuring the child route is
 * currently active, otherwise an error will be thrown.
 *
 * Overrides for query configuration can be optionally passed as a third argument
 *
 * Data will always be undefined, as it is not known whether or not the child route
 * is rendered
 */
export function useChildRouteQuery<
  Config extends QueryRoute<
    string,
    string,
    string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Record<string, QueryRouteQueryConfig<string, any, any, any, any, any, any>>
  >,
  QueryName extends string & keyof Config['queries'],
  TData = DefaultQueryData<Config, QueryName>,
>(route: Config, queryName: QueryName, queryOverrides?: QueryOverridesFor<Config, QueryName, TData>) {
  // A parent cannot guarantee that the child route it reads has produced loader data — the child's
  // loader may have failed, or the match may exist before it settles. Both are states this hook
  // already promises to represent as `data: undefined`, so resolve the config optionally and feed
  // the query a `skipToken` when it is absent. That keeps the hook order stable while letting the
  // caller branch on `data` instead of needing an error boundary to contain an exception.
  const config = useOptionalQueriesConfig(route, queryName, {allowReadFromChildRoutes: true})
  /**
   * If we have a deferred query we don't have initial data always defined, so it potentially
   * could render with `undefined`.  When we have a blocking query, the loader awaits the data
   * so it should never be undefined
   */
  // `useStreamingCompatibleQuery` is a no-op wrapper around `useQuery` when
  // `suppressDuringSSR` is false (the canonical case). Routing through it
  // here lets streaming-capable routes (UI Service-registered ones, where the
  // loader sets `suppressDuringSSR: true` on the loaderData entry) get SSR
  // suppression automatically without a separate code path.
  return withQueryKey(
    useStreamingCompatibleQuery(
      // @ts-expect-error not sure
      config
        ? {...config.queryConfig, ...queryOverrides}
        : {queryKey: ['route-data-unavailable', route.id, queryName], queryFn: skipToken},
      config?.suppressDuringSSR ?? false,
    ),
    config?.queryConfig.queryKey,
  ) as UseQueryResult<TData> & {
    queryKey: RouteQueryKey | undefined
  }
}

/**
 * Given a named route query and a route, returns the
 * result of calling useSuspenseQuery on the generated route config.
 *
 * Should be used in scenarios where a parent route's component wants to access data from
 * a child route. The parent component is responsible for ensuring the child route is
 * currently active, otherwise an error will be thrown.
 *
 * Overrides for query configuration can be optionally passed as a third argument
 */
export function useSuspenseChildRouteQuery<
  Config extends QueryRoute<
    string,
    string,
    string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Record<string, QueryRouteQueryConfig<string, any, any, any, any, any, any>>
  >,
  QueryName extends string & keyof Config['queries'],
  TData = DefaultQueryData<Config, QueryName>,
>(route: Config, queryName: QueryName, queryOverrides?: SuspenseQueryOverridesFor<Config, QueryName, TData>) {
  const {queryConfig} = useQueriesConfig(route, queryName, {allowReadFromChildRoutes: true})
  /**
   * If we have a deferred query we don't have initial data always defined, so it potentially
   * could render with `undefined`.  When we have a blocking query, the loader awaits the data
   * so it should never be undefined
   */
  return withQueryKey(
    useSuspenseQuery(
      // @ts-expect-error not sure
      {...queryConfig, ...queryOverrides},
    ),
    queryConfig.queryKey,
  ) as UseSuspenseQueryResult<TData> & {
    queryKey: RouteQueryKey
  }
}
